import {getManager} from "../../../ts/manager/Manager";
import React, {useState} from "react";
import LoadingPartial from "../../partial/status/LoadingPartial";
import LocateListForm from "../../form/locate/LocateListForm";
import {
    LocateDataModel,
    LocateReturnType,
    LocateViewSortBy
} from "../../../ts/data/locate/LocateDataFactory";
import NoContent from "../../partial/status/NoContent";
import {IonCard, IonInfiniteScroll, IonInfiniteScrollContent} from "@ionic/react";
import StateViewController from "../../vc/StateViewController";
import {assert} from "../../../ts/common/Assert";
import {IRouteProps} from "../../../ts/manager/view/ViewInterface";
import {dispatchRedirectTo} from "../../../ts/event/DispatchEvent";
import RoutePagination from "../../element/RoutePagination";
import SearchViewModel from "../search/SearchViewModel";
import {IonInfiniteScrollCustomEvent} from "@ionic/core/dist/types/components";
import {LocatePageListRouteModel} from "../../../ts/route/locate/LocatePageListRouteFactory";
import {LngLat} from "maplibre-gl";
import {LocatableDataChoice} from "../../../ts/data/locate/LocatableDataFactory";

interface IProps extends IRouteProps
{
    getCardContent:(locateChoice:LocatableDataChoice, fromLngLat:LngLat|null, index:number, tally:number) => React.JSX.Element|null
    returnType?:LocateReturnType
    nearChoice?:LocatableDataChoice|null
    nearPositionText?:string,
    preventInfiniteScroll?:boolean,
}

const LocatePageListPage = (props:IProps) =>
{
    const primaryPageNumber = parseInt(getManager().navigation.model.routeIDs.locate_page_list_route_id ?? "1");
    const returnType:LocateReturnType = props.returnType ?? "basic_reference_object";

    const [viewModel] = useState<SearchViewModel>(() => {
        return new SearchViewModel();
    });
    viewModel.populate({
        pageListRouteModel: getManager().navigation.model.routeModels.locatePageList,
        primaryPageNumber: primaryPageNumber,
        returnType: returnType,
        preventInfiniteScroll: !!props.preventInfiniteScroll
    })
    const [, setSimpleState] = useState<any[]>([]);
    const [hideResults, setHideResults] = useState<boolean>(false);

    const pageListRouteModel = viewModel.pageListRouteModel as LocatePageListRouteModel;
    const primaryPageModel = (viewModel.getPrimaryPageModel() || null) as LocateDataModel|null;
    const locateCountryModel = primaryPageModel?.getCountryModel() ?? null;

    const postType = getManager().navigation.model.routeIDs.post_type ?? null;


    const viewState = StateViewController({
        routeProps: props,
        assert: [pageListRouteModel, primaryPageModel, locateCountryModel],
        dataModels: [locateCountryModel, primaryPageModel]
    })

    if(viewState.overrideView){
        return viewState.overrideView;
    }

    assert(pageListRouteModel);
    assert(primaryPageModel);
    assert(locateCountryModel);

    const countryChoiceList = locateCountryModel.choiceList();

    if (!countryChoiceList.length)
    {
        return <NoContent {...props}/>;
    }

    viewModel.requireInitialized().then((didInitialize:boolean) => {
        if(didInitialize){
            setSimpleState([]);
        }
    })

    viewModel.update();

    const pageChoiceList = viewModel.pageChoiceList;
    const locateChoiceList = viewModel.itemChoiceList as LocatableDataChoice[];
    const pageChoice = viewModel.pageChoice;

    const usePages = viewModel.usePages;
    const showPages = viewModel.showPages;
    const showInfiniteScroll = viewModel.showInfiniteScroll;
    const numberOfEntries = viewModel.numberOfEntries;

    const hasSearch = !!viewModel.searchPageDataModel?.searchParams.searchTermID
    const searchParams = pageListRouteModel.searchParams;
    const fromLngLat = searchParams.latitude || searchParams.longitude ? new LngLat(searchParams.longitude, searchParams.latitude) : null;


    const sortByOptions:LocateViewSortBy[] = !props.nearChoice ? ["name", "near_me", "near_location"] : ["name", "near_me", "near_position", "near_location"];
    let sortBy:LocateViewSortBy = (getManager().navigation.currentParams.sort_by as LocateViewSortBy) ?? "name";
    sortBy = sortByOptions.includes(sortBy as LocateViewSortBy) ? sortBy as LocateViewSortBy : "name";
    let nearCountryCode = "";
    let town = "";
    let placeID = 0;
    if(sortBy === "near_location"){
        nearCountryCode = decodeURIComponent(getManager().navigation.currentParams.near_country ?? "").toLowerCase();
        town = decodeURIComponent(getManager().navigation.currentParams.town ?? "");
        placeID = parseInt(getManager().navigation.currentParams.place_id ?? "0");
    }

    const onInfiniteScrollNext = (event:IonInfiniteScrollCustomEvent<void>) =>{
        viewModel.checkAddNextInfiniteScrollPage((completed) => {
            if(completed){
                setSimpleState([])
                event.target.complete();
            }
        })
    }

    const onSearchSubmit = (countryCode:string, latitude:number, longitude:number, searchTerm:string, sortBy:LocateViewSortBy, nearCountryCode:string, town:string, placeID:number) =>
    {
        let query = (searchTerm ? "s=" + searchTerm : "all=1");
        if(countryCode)
        {
            query += "&country=" + countryCode;
        }
        if(latitude || longitude)
        {
            query += "&lat=" + latitude + "&lng=" + longitude;
        }
        if(sortBy && sortBy !== "name"){
            query += "&sort_by=" + sortBy;
        }

        if(nearCountryCode){
            query += "&near_country=" + nearCountryCode;
        }
        if(town){
            query += "&town=" + town;
        }
        if(placeID){
            query += "&place_id=" + placeID;
        }
        const baseURL = props.choice.baseURL;
        let path = baseURL + "?" + query

        dispatchRedirectTo(path, false, false, () => {
            setHideResults(false);
            const pageChoice = getManager().navigation.model.routeModels.locatePageList?.parentChoice() ?? null;
            if(pageChoice){
                getManager().ui.findRouteViewChoice(pageChoice.realPath);
            }
        })
    }

    const onAnyChange = () =>
    {
        setHideResults(true);
    }
    return <>
        <section className={"content-block "+(!viewState.hasCurrentData ? "content-disabled" : "")}>
            <LocateListForm
                routeProps={props}
                key={pageListRouteModel.id+"&sortBy="+sortBy+(nearCountryCode ? "&nearCountryCode="+nearCountryCode : "")+(town ? "&town="+town : "")+(placeID ? "&placeID="+placeID : "")}
                postType={searchParams.postType ?? null}
                countryChoiceList={countryChoiceList}
                countryCode={searchParams.countryCode}
                searchTermID={searchParams.searchTermID}
                latitude={searchParams.latitude}
                longitude={searchParams.longitude}
                sortBy={sortBy}
                nearCountryCode={nearCountryCode}
                town={town}
                placeID={placeID}
                requireLocation={false}
                returnType={returnType}
                nearChoice={props.nearChoice}
                nearPositionText={props.nearPositionText}
                onSearchSubmit={onSearchSubmit}
                onAnyChange={onAnyChange}
            />
        </section>

        {!viewState.hasCurrentData || !viewModel.initialized ?
            <>
                <LoadingPartial {...props}/>
            </>
            :
            <>
                {hasSearch && !hideResults && <>
                    <section className={"content-block"}>
                        <div
                            className="container"
                            data-route-view={pageChoice?.realPath}
                        >
                            <div className="row">
                                <div className="col-12 text-end">
                                    <p className="smaller mb-2">
                                        {numberOfEntries > 1 ? numberOfEntries+" entries" : (numberOfEntries ? "1 entry" : "No entries")}
                                    </p>
                                </div>
                            </div>

                            {usePages && <>
                                <div className={"row" + (!showPages ? " appear-hidden" : "")}>
                                    <div className="col-12 text-end">
                                        <RoutePagination choiceList={pageChoiceList} />
                                    </div>
                                </div>
                            </>}
                            <div className="row mt-4">
                                <div className="col-12">

                                    {locateChoiceList.map((locateChoice, index) =>
                                    {
                                        const content = props.getCardContent(locateChoice, fromLngLat, index, locateChoiceList.length);
                                        if(!content){
                                            return "";
                                        }
                                        return <IonCard
                                            key={locateChoice.id}
                                        >
                                            {content}
                                        </IonCard>
                                    })}
                                </div>
                            </div>
                            {showInfiniteScroll && <>
                                <IonInfiniteScroll
                                    onIonInfinite={onInfiniteScrollNext}
                                >
                                    <IonInfiniteScrollContent loadingText="Please wait..." loadingSpinner="bubbles"></IonInfiniteScrollContent>
                                </IonInfiniteScroll>
                            </>}
                        </div>
                    </section>
                </>}
            </>
        }

    </>
}

export default LocatePageListPage;